import React from "react";
import PropTypes from "prop-types";
import i18next from "i18next";

/**
 * Generic error boundary.
 *
 * Catches render-time exceptions in the subtree and shows a small fallback UI
 * with a reload button instead of letting the whole app unmount to a blank
 * white page. Use it to wrap pages/sections where a single bad value from an
 * API (e.g. missing exchange rate) should not take the entire screen down.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log for debugging; the UI stays usable.
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h4 style={{ marginBottom: "12px" }}>
            {i18next.t("Something went wrong while loading this page.")}
          </h4>
          <p style={{ marginBottom: "20px", color: "#666" }}>
            {i18next.t("Please reload the page. If the problem persists, contact support.")}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.handleReload}
          >
            {i18next.t("Reload")}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
};

export default ErrorBoundary;
